import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  TextField,
  IconButton,
  Snackbar,
} from "@mui/material";
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { get, put } from "src/utils/networkUtils";
import { fDateTime } from "src/utils/formatTime";
import { calculateProfit } from "src/utils/profitCalculator";

export default function MastersMissionsHistory() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState({});
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({ startDate: "", endDate: "" });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const fetchMatchMasters = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await get("/admin/minigames/match-masters");
        setData(Array.isArray(response.missions) ? response.missions : []);
      } catch (err) {
        console.error("Error fetching match masters data:", err);
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchMatchMasters();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  const handleLeaderboard = async (contestId, missionType, rowId) => {
    try {
      const compositeKey = `${contestId}-${missionType}`;

      // Toggle row - if clicking on already expanded row, collapse it
      if (expandedRow === compositeKey) {
        setExpandedRow(null);
        return;
      }

      // Don't fetch if we already have data for this row
      if (!leaderboardData[compositeKey]) {
        setLoadingLeaderboard(compositeKey);
        const response = await get(`/admin/minigames/match-masters/leaderboard?contestId=${contestId}&missionType=${missionType}`);
        setLeaderboardData(prev => ({
          ...prev,
          [compositeKey]: response.leaderboard || []
        }));
        setLoadingLeaderboard(null);
      }

      setExpandedRow(compositeKey);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setLoadingLeaderboard(null);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const handleEditClick = (row) => {
    setEditingRow(row.contestId);
    setEditData({
      startDate: formatDateForInput(row.startDate),
      endDate: formatDateForInput(row.endDate),
    });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({ startDate: "", endDate: "" });
  };

  const handleSaveEdit = async (contestId) => {
    try {
      setSaving(true);
      await put(`/admin/minigames/match-masters/contest/${contestId}`, {
        startDate: editData.startDate + ':00.000Z',
        endDate: editData.endDate + ':00.000Z',
      });

      // Update local data
      setData(prevData =>
        prevData.map(row =>
          row.contestId === contestId
            ? { ...row, startDate: editData.startDate, endDate: editData.endDate }
            : row
        )
      );

      setEditingRow(null);
      setEditData({ startDate: "", endDate: "" });
      setSnackbar({ open: true, message: "Contest updated successfully", severity: "success" });
    } catch (err) {
      console.error("Error updating contest:", err);
      setSnackbar({ open: true, message: err.message || "Failed to update contest", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Match Masters Contests
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {data.length} contest(s) found
        </Typography>
      </Box>

      <Card>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600 }}>Contest ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Entry</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Users Count</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Profit</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grid Size Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Prize Breakdown</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      sx={{
                        "&:hover": { backgroundColor: "#f8f9fa" },
                      }}
                    >
                      <TableCell>{row.contestId}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.entry}</TableCell>
                      <TableCell>{row.usersCount}</TableCell>
                      <TableCell>{calculateProfit(row)}</TableCell>
                      <TableCell>{row.gridSizeType}</TableCell>
                      <TableCell>
                        {editingRow === row.contestId ? (
                          <TextField
                            type="datetime-local"
                            size="small"
                            value={editData.startDate}
                            onChange={(e) => setEditData({ ...editData, startDate: e.target.value })}
                            sx={{ width: 200 }}
                          />
                        ) : (
                          fDateTime(row.startDate)
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRow === row.contestId ? (
                          <TextField
                            type="datetime-local"
                            size="small"
                            value={editData.endDate}
                            onChange={(e) => setEditData({ ...editData, endDate: e.target.value })}
                            sx={{ width: 200 }}
                          />
                        ) : (
                          fDateTime(row.endDate)
                        )}
                      </TableCell>
                      <TableCell>{row.prizeBreakdown}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          {editingRow === row.contestId ? (
                            <>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleSaveEdit(row.contestId)}
                                disabled={saving}
                              >
                                {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={handleCancelEdit}
                                disabled={saving}
                              >
                                <CancelIcon />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEditClick(row)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {row.usersCount > 0 && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleLeaderboard(row.contestId, row.missionType, row.contestId)}
                              disabled={loadingLeaderboard === `${row.contestId}-${row.missionType}`}
                              startIcon={loadingLeaderboard === `${row.contestId}-${row.missionType}` ? <CircularProgress size={16} /> : null}
                            >
                              {loadingLeaderboard === `${row.contestId}-${row.missionType}`
                                ? "Loading..."
                                : expandedRow === `${row.contestId}-${row.missionType}`
                                  ? "Hide Leaderboard"
                                  : "Show Leaderboard"}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {expandedRow === `${row.contestId}-${row.missionType}` && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                          <Collapse in={true} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div">
                                Leaderboard
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Rank</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Play ID</TableCell>
                                    <TableCell>Time (s)</TableCell>
                                    <TableCell>Moves</TableCell>
                                    <TableCell>Winnings</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {leaderboardData[`${row.contestId}-${row.missionType}`]?.length > 0 ? (
                                    leaderboardData[`${row.contestId}-${row.missionType}`].map((player) => (
                                      <TableRow key={player.playId}>
                                        <TableCell>{player.rank}</TableCell>
                                        <TableCell>{player.username}</TableCell>
                                        <TableCell>{player.playId}</TableCell>
                                        <TableCell>{player.time}</TableCell>
                                        <TableCell>{player.moves}</TableCell>
                                        <TableCell>{player.winnings}</TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          No leaderboard data available
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
